import * as React from 'react';
import { useMemo } from 'react';

import { MultiSelect } from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import { useSitesForSelect } from '../../sites/hooks';
import type { UseSitesForSelectOptions } from '../../sites/hooks';
import type { CarrierSiteFilter } from '../../carriers/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SpaceSitesSelectorProps {
  /**
   * Resolved labels for the user's existing site scopes (with their parent space). Lets a drifted
   * site stay visible/selected via the escape hatch without being offered as a re-selectable option.
   */
  existingSiteOptions?: { label: string; parentSpaceId?: string; value: string }[];
  isSiteLocked: boolean;
  label: string;
  lockedSiteId?: string | null;
  noResultsText: string;
  onSiteSelectionChange: (siteIds: string[]) => void;
  placeholder: string;
  /**
   * The current selected site IDs for this space (the live form value, post-prune). Sites belonging
   * to this space are shown as selected chips. Drift sites (saved sites no longer in the carrier's
   * enabled set) remain visible/removable via the escape hatch.
   */
  selectedSiteIds: string[];
  /**
   * Optional carrier-derived site constraint for this space. When provided, only the carrier's
   * enabled sites are offered (used by the driver scope selector). When omitted, all sites in the
   * space are offered (default — e.g. the invite-user flow).
   */
  siteFilter?: CarrierSiteFilter;
  spaceId: string;
  spaceName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SpaceSitesSelector({
  existingSiteOptions,
  isSiteLocked,
  label,
  lockedSiteId,
  noResultsText,
  onSiteSelectionChange,
  placeholder,
  selectedSiteIds,
  siteFilter,
  spaceId,
  spaceName,
}: SpaceSitesSelectorProps): React.JSX.Element {
  const siteSelectOptions = useMemo<UseSitesForSelectOptions>(() => {
    if (!siteFilter) {
      return { spaceIds: [spaceId] };
    }
    if (siteFilter.mode === 'allExcept') {
      return { excludeIds: siteFilter.excludeIds, spaceIds: [spaceId] };
    }
    if (siteFilter.mode === 'only') {
      return { ids: siteFilter.ids, spaceIds: [spaceId] };
    }
    // mode === 'none' → carrier is not enabled in this space; offer no sites.
    return { ids: [], spaceIds: [spaceId] };
  }, [siteFilter, spaceId]);

  const { options: siteOptions } = useSitesForSelect(siteSelectOptions);

  // Compute the selected IDs that belong to this space: those offered by the carrier filter,
  // plus any that have drifted outside it (matched via the resolved scope's parentSpaceId).
  // This is derived entirely from the incoming selectedSiteIds (the live form value).
  const selectedForSpace = useMemo(
    () =>
      selectedSiteIds.filter(
        (id) =>
          siteOptions.some((o) => o.value === id) ||
          (existingSiteOptions ?? []).some((o) => o.value === id && o.parentSpaceId === spaceId),
      ),
    [selectedSiteIds, existingSiteOptions, siteOptions, spaceId],
  );

  // Drifted sites: selected for this space but no longer offered by the carrier filter.
  // They stay visible/removable as extra chips without being re-selectable.
  const driftedSiteOptions = useMemo<MultiSelectOption[]>(
    () =>
      selectedForSpace
        .filter((id) => !siteOptions.some((o) => o.value === id))
        .flatMap((id) => {
          const option = (existingSiteOptions ?? []).find((o) => o.value === id);
          return option ? [{ label: option.label, value: option.value }] : [];
        }),
    [selectedForSpace, existingSiteOptions, siteOptions],
  );

  const controlledValue = lockedSiteId ? [lockedSiteId] : selectedForSpace;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
      <p className="text-sm font-medium">{spaceName}</p>
      <MultiSelect
        disabled={isSiteLocked}
        emptyText={noResultsText}
        extraSelectedOptions={driftedSiteOptions}
        hideFooter
        label={label}
        onValueChange={onSiteSelectionChange}
        options={siteOptions}
        placeholder={placeholder}
        showLabelWhenFiltering
        value={controlledValue}
      />
    </div>
  );
}
